// Windowed leave + holiday reads for the month/week grids.
//
// Both reads take a CALENDAR-DATE window (inclusive "YYYY-MM-DD" bounds): leave
// start/end and holiday days are tenant-local whole days, so no zone applies.
// The leave list is narrowed server-side (`from`/`to`) and drained page by page,
// so a month never renders empty because its rows sit beyond the first pages of
// the created_at keyset order.

import { useQuery } from "@tanstack/react-query";

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";
import { collectKeysetWindow, type KeysetPage, type KeysetWindow } from "@/api/keyset";
import type { DayKey } from "@/lib/calendar-window";

import type { LeaveRequestRow } from "./leave-api";

export interface DateWindow {
  readonly from: DayKey;
  readonly to: DayKey;
}

/** One holiday day as GET /v1/leave/holidays returns it. */
export interface HolidayDay {
  readonly holiday_id: string;
  readonly date: DayKey;
  readonly name: string;
  readonly kind: string;
}

// A month of leave in a large tenant is still far below this; reaching it is
// reported to the viewer (truncated) instead of dropping days silently.
export const LEAVE_WINDOW_CAP = 2000;
const PAGE_LIMIT = 500;

// openapi-fetch has no typed query for these schemaless routes; the cast keeps
// the canonical envelope + keyset contract explicit at the call site.
type LeaveWindowGet = (
  path: "/v1/leave/requests",
  init: { params: { query: { from: string; to: string; limit: number; cursor?: string; mine?: boolean } } },
) => Promise<EnvelopedFetchResult<KeysetPage<LeaveRequestRow>>>;

type HolidaysGet = (
  path: "/v1/leave/holidays",
  init: { params: { query: { from: string; to: string } } },
) => Promise<EnvelopedFetchResult<{ items: HolidayDay[] }>>;

export function fetchLeaveWindow(
  window: DateWindow,
  mine: boolean,
): Promise<KeysetWindow<LeaveRequestRow>> {
  const get = platformGraph.GET as unknown as LeaveWindowGet;
  return collectKeysetWindow(
    (cursor) =>
      unwrapEnvelope(
        get("/v1/leave/requests", {
          params: {
            query: {
              from: window.from,
              to: window.to,
              limit: PAGE_LIMIT,
              ...(cursor !== null && { cursor }),
              ...(mine && { mine: true }),
            },
          },
        }),
      ),
    LEAVE_WINDOW_CAP,
  );
}

export async function fetchHolidays(window: DateWindow): Promise<readonly HolidayDay[]> {
  const get = platformGraph.GET as unknown as HolidaysGet;
  const data = await unwrapEnvelope(
    get("/v1/leave/holidays", { params: { query: { from: window.from, to: window.to } } }),
  );
  return data.items;
}

export function useLeaveWindow(window: DateWindow, mine: boolean, enabled = true) {
  return useQuery({
    queryKey: ["leave", "requests", "window", mine ? "mine" : "scoped", window.from, window.to],
    queryFn: () => fetchLeaveWindow(window, mine),
    enabled,
  });
}

export function useHolidays(window: DateWindow, enabled = true) {
  return useQuery({
    queryKey: ["leave", "holidays", window.from, window.to],
    queryFn: () => fetchHolidays(window),
    staleTime: 5 * 60_000,
    enabled,
  });
}
